/*
 * Copyright (c) 2026 Bolmate B.V.
 *
 * Shared "how to (re-)enable notifications" guide dialog. Used by the admin
 * push card (suppressed-prompt hint) AND by the per-notification channels
 * select (checking "Dit apparaat" while the browser permission is denied) —
 * regular users no longer see the card, so the recovery steps must be
 * reachable from the select itself.
 *
 * Rendered as a BolmateDialog (same chrome/footer as every other dialog)
 * through a one-off React root, so callers keep the fire-and-forget
 * `showPushPermissionGuide(t, variant)` call from their event handlers.
 */

import React, {useState} from 'react';

import {createRoot} from 'react-dom/client';

import type {TFunction} from 'i18next';

import BolmateDialog from '../controls/dialog/bolmateDialog';

import {isAndroid, isIOS, isMobileDevice, isStandalone} from '../../util/pwaUtil';

export type PushPermissionGuideVariant = 'denied' | 'suppressed';

/** Platform-specific steps for re-enabling notifications via the browser/OS
 *  settings. The final step points at the channels select — that is where
 *  (re)enabling lives. */
export function buildPushPermissionSteps(t: TFunction): Array<string> {
    if (isIOS()) {
        return [
            t('push.deniedHelp.ios1', 'Open de app Instellingen op je iPhone of iPad.'),
            t('push.deniedHelp.ios2', "Ga naar 'Meldingen' en zoek 'Bolmate' in de lijst."),
            t(
                'push.deniedHelp.ios3',
                "Zet 'Sta meldingen toe' aan, kom terug en vink dit apparaat opnieuw aan bij een notificatie."
            )
        ];
    }
    if (isAndroid()) {
        // Installed PWA on Android (WebAPK) has no address bar / lock icon —
        // notifications are managed by the OS, not Chrome's site settings, so
        // route the user to the Android app notification settings instead.
        if (isStandalone()) {
            return [
                t(
                    'push.deniedHelp.androidPwa1',
                    "Houd het Bolmate-icoon op je beginscherm ingedrukt → 'App-info' (of open Android-instellingen → 'Apps' → 'Bolmate')."
                ),
                t('push.deniedHelp.androidPwa2', "Tik op 'Meldingen'."),
                t(
                    'push.deniedHelp.androidPwa3',
                    'Zet meldingen aan, kom terug in de app en vink dit apparaat opnieuw aan bij een notificatie.'
                )
            ];
        }
        return [
            t(
                'push.deniedHelp.android1',
                'Tik op het slot-icoon (of ⋮ → Site-informatie) naast de adresbalk bovenaan.'
            ),
            t('push.deniedHelp.android2', "Open 'Machtigingen' → 'Meldingen'."),
            t(
                'push.deniedHelp.android3',
                "Zet het op 'Toestaan' (of 'Rechten resetten'), kom terug en vink dit apparaat opnieuw aan bij een notificatie."
            )
        ];
    }
    return [
        t('push.deniedHelp.desktop1', 'Klik op het slot-icoon links van de adresbalk.'),
        t(
            'push.deniedHelp.desktop2',
            "Zet 'Meldingen' op 'Toestaan', vernieuw de pagina en vink dit apparaat opnieuw aan bij een notificatie."
        )
    ];
}

function buildIntro(t: TFunction, variant: PushPermissionGuideVariant): string {
    if (variant === 'denied') {
        return t(
            'push.deniedHelp.intro',
            'Meldingen zijn geblokkeerd in je browser- of systeeminstellingen. Zo zet je ze weer aan:'
        );
    }
    // Suppressed: the browser will not re-show the prompt after it was
    // dismissed/ignored — nothing is broken yet, so the copy is softer.
    if (isAndroid()) {
        return t(
            'push.suppressedHelp.androidIntro',
            'Je browser toont de meldingsvraag niet opnieuw nadat deze is weggeklikt. Zo zet je meldingen alsnog aan:'
        );
    }
    if (isIOS()) {
        return t(
            'push.suppressedHelp.iosIntro',
            'De meldingsvraag wordt niet opnieuw getoond nadat deze is weggeklikt. Zo zet je meldingen alsnog aan:'
        );
    }
    return t(
        'push.suppressedHelp.desktopIntro',
        'Je browser toont de meldingsvraag niet opnieuw nadat deze is weggeklikt. Zo zet je meldingen alsnog aan:'
    );
}

/** Closing hint for the case the steps do NOT cover: the setting already reads
 *  "allowed" while the browser still reports the permission as denied/blocked.
 *  That happens when the OS-level switch and the site permission disagree (an
 *  Android app-notification toggle reset, a macOS/Windows "Notifications off
 *  for this browser" state, a permission left over from an uninstalled PWA) —
 *  toggling the setting off and on again re-syncs it. */
function buildAlreadyOnNote(t: TFunction): string {
    if (isMobileDevice()) {
        return t(
            'push.deniedHelp.alreadyOnMobile',
            'Staan meldingen daar al aan? Zet ze dan één keer UIT en weer AAN in de systeeminstellingen, open de app opnieuw en vink dit apparaat opnieuw aan.'
        );
    }
    return t(
        'push.deniedHelp.alreadyOnDesktop',
        "Staat het al op 'Toestaan'? Zet meldingen dan één keer UIT en weer AAN — in je browserinstellingen én in de systeeminstellingen van je computer — vernieuw daarna de pagina."
    );
}

interface GuideProps {
    t: TFunction;
    variant: PushPermissionGuideVariant;
    /** Called after the dialog closed — unmounts the one-off root. */
    onDone: VoidFunction;
}

function PushPermissionGuideDialog(props: GuideProps) {
    const {t, variant, onDone} = props;
    const [active, setActive] = useState(true);
    const steps = buildPushPermissionSteps(t);

    function onClose() {
        setActive(false);
        onDone();
    }

    return (
        <BolmateDialog
            active={active}
            onClose={onClose}
            title={t('push.suppressedHelp.title', 'Meldingen inschakelen')}
            className='push-permission-guide'
            onConfirm={onClose}
            confirmLabel={t('push.deniedHelp.gotIt', 'Begrepen')}
            hideCancel
        >
            <div className='push-denied-help'>
                <p>{buildIntro(t, variant)}</p>
                <ol>
                    {steps.map((step, i) => (
                        <li key={i}>{step}</li>
                    ))}
                </ol>
                <p className='push-denied-help-note'>{buildAlreadyOnNote(t)}</p>
            </div>
        </BolmateDialog>
    );
}

export function showPushPermissionGuide(t: TFunction, variant: PushPermissionGuideVariant): void {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    // Deferred so the unmount never runs inside the root's own event/render
    // cycle (React warns on synchronous unmounts from within).
    const onDone = () =>
        window.setTimeout(() => {
            root.unmount();
            host.remove();
        }, 0);
    root.render(<PushPermissionGuideDialog t={t} variant={variant} onDone={onDone} />);
}
